import axios from "axios";
import getBaseUrl from "./utils/getBaseUrl";
import { accessUserId } from "./utils/auth";
import {prepareLegacyPaidRequest,settleLegacyPaidRequest} from './utils/legacyPaidRequest';

const api = axios.create({
  baseURL: getBaseUrl(),
  withCredentials: true,
});

api.interceptors.request.use(async (config) => {
  const t = sessionStorage.getItem("doar_token");
  if (t) {
    config.headers = config.headers || {};
    // An explicit credential belongs to the request that captured it (not
    // whichever account is active by the time Axios dispatches the request).
    if (!config.headers.Authorization) config.headers.Authorization = `Bearer ${t}`;
  }
  const bearer = String(config.headers?.Authorization || "").replace(/^Bearer\s+/i, "");
  config.__doarRequestAccountId = accessUserId(bearer);
  return prepareLegacyPaidRequest(config);
});

let _refreshPromise = null;

const REFRESHABLE_CODES = new Set(["TOKEN_EXPIRED"]);
const FATAL_AUTH_CODES = new Set(["REUSE_DETECTED", "SECURITY_MISMATCH", "PASSWORD_CHANGED", "SESSION_REVOKED"]);
const AI_PLAN_LIMIT_CODES = new Set(["AI_LIMIT_REACHED", "AI_CREDITS_REQUIRED"]);

api.interceptors.response.use(
  (res) => settleLegacyPaidRequest(res),
  async (error) => {
    const original = error.config || {};
    const data = error.response?.data;
    const code = data?.code;
    const requestedId = original.__doarRequestAccountId;
    const selectedId = accessUserId(sessionStorage.getItem("doar_token"));
    // A late response from another account must never clear or refresh this one.
    if (requestedId && requestedId !== selectedId) return Promise.reject(error);

    // Refresh requests must never enter the refresh interceptor again. Without
    // this guard, a 401 from /auth/refresh can await its own _refreshPromise
    // forever and leave UI actions (including Publish) stuck in a loading state.
    if (original._skipAuthRefresh) {
      return Promise.reject(error);
    }

    if (error.response?.status === 429) {
      const reason = AI_PLAN_LIMIT_CODES.has(code) ? "plan_limit" : "rate_limit";
      window.dispatchEvent(new CustomEvent("ai:limit-reached", {
        detail: { reason, code, message: data?.message, usage: data?.usage || null },
      }));
    }

    if (error.response?.status === 401 && FATAL_AUTH_CODES.has(code)) {
      sessionStorage.removeItem("doar_token");
      window.dispatchEvent(new CustomEvent("auth:force-logout", { detail: { code, userId: requestedId || selectedId } }));
      return Promise.reject(error);
    }

    const isRefreshable = REFRESHABLE_CODES.has(code) ||
      (!code && !!sessionStorage.getItem("doar_token"));
    if (
      error.response?.status === 401 &&
      isRefreshable &&
      !original._retry
    ) {
      original._retry = true;

      const currentAccount = accessUserId(sessionStorage.getItem("doar_token"));
      if (!currentAccount || (requestedId && requestedId !== currentAccount) ||
          sessionStorage.getItem("doar_signed_out") === "1") return Promise.reject(error);
      if (!_refreshPromise || _refreshPromise.accountId !== currentAccount) {
        const attempt = api.post("/auth/refresh", null, {
          timeout: 10000, _skipAuthRefresh: true,
          headers: { "X-Doar-Account-Id": currentAccount },
        }).then(({ data: d }) => {
          const newToken = d?.token || d?.accessToken;
          if (!newToken || accessUserId(newToken) !== currentAccount ||
              accessUserId(sessionStorage.getItem("doar_token")) !== currentAccount ||
              sessionStorage.getItem("doar_signed_out") === "1") {
            throw new Error("Account changed while refreshing.");
          }
          sessionStorage.setItem("doar_token", newToken);
          // Keep React auth state and its expiry timer in sync with Axios.
          window.dispatchEvent(new CustomEvent("auth:token-refreshed", {
            detail: { userId: currentAccount, token: newToken },
          }));
          return newToken;
        }).catch((refreshError) => {
          // A raced refresh (409), service outage or transient network error
          // must never destroy another live session or claim a revocation.
          if (refreshError?.response?.status === 401 &&
              accessUserId(sessionStorage.getItem("doar_token")) === currentAccount) {
            sessionStorage.removeItem("doar_token");
            window.dispatchEvent(new CustomEvent("auth:force-logout", {
              detail: { code: refreshError?.response?.data?.code || "SESSION_EXPIRED", userId: currentAccount },
            }));
          }
          throw refreshError;
        }).finally(() => {
          if (_refreshPromise?.promise === attempt) _refreshPromise = null;
        });
        _refreshPromise = { accountId: currentAccount, promise: attempt };
      }

      try {
        const newToken = await _refreshPromise.promise;
        if (accessUserId(sessionStorage.getItem("doar_token")) !== currentAccount) return Promise.reject(error);
        original.headers = original.headers || {};
        original.headers.Authorization = `Bearer ${newToken}`;
        return api(original);
      } catch {
        return Promise.reject(error);
      }
    }

    return Promise.reject(error);
  }
);

export const login          = (data) => api.post("/auth/login", data);
export const register       = (data) => api.post("/auth/register", data);
export const logout         = (accessToken = null) => api.post("/auth/logout", null, {
  ...(accessToken ? { headers: { Authorization: `Bearer ${accessToken}` } } : {}),
  // Signing out must not silently refresh or adopt a different account token.
  _skipAuthRefresh: true,
});
export const logoutAll      = (accessToken = null) => api.post("/auth/logout-all", null, {
  ...(accessToken ? { headers: { Authorization: `Bearer ${accessToken}` } } : {}),
  _skipAuthRefresh: true,
});
export const forgotPassword = (email) => api.post("/auth/forgot-password", { email });
export const verifyEmail    = (token) => api.get(`/auth/verify-email?token=${encodeURIComponent(token)}`);

export const resetPassword = (uid, token, password) =>
  api.post("/auth/reset-password", { uid, token, password });

export const changePassword = (currentPassword, newPassword) =>
  api.post("/auth/change-password", { currentPassword, newPassword });

export const uploadModel  = (formData) =>
  api.post("/model/upload", formData, { headers: { "Content-Type": "multipart/form-data" } });
export const fetchModels  = ()         => api.get("/model/list");
export const deleteModel  = (id)       => api.delete(`/model/${id}`);
export const renameModel  = (id, name) => api.put(`/model/${id}/rename`, { newName: name });
export const getModelFileUrl = async (uploadId, fileName) => {
  const res = await api.get(`/model/file/${uploadId}/${fileName}`);
  return res.data.url;
};

export const listProjectViews  = (projectId)       => api.get(`/project/${projectId}/views`);
export const createProjectView = (projectId, data) => api.post(`/project/${projectId}/views`, data);
export const updateProjectView = (viewId, data)    => api.put(`/project/views/${viewId}`, data);

export const fetchChatMessages = (modelId)       => api.get(`/chat/${modelId}`);
export const saveChatMessage   = (modelId, data) => api.post(`/chat/${modelId}`, data);

export const generateVideo = (data) => api.post("/videoGenAI", data);

export default api;
